import { lazy, useEffect, useState } from 'react';
import { Location, useLocation, useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { logEvent } from '@amplitude/analytics-browser';
import { camelize } from 'humps';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Layout from '../../componentsv2/Layout';
import Typography from '../../componentsv2/Typography';
import Button from '../../componentsv2/Button';
import InfoButton from '../../componentsv2/InfoButton';
import Section from '../../componentsv2/Section';
import StatusTracker from '../../componentsv2/StatusTracker';
import PageHeader from '../../componentsv2/PageHeader';
import Chip from '../../componentsv2/Chip';
import FinanceInvoices from '../../componentsv2/FinanceInvoices';
import {
  EyeIcon,
  SparklingFolderIcon,
  StarIcon,
  TrashIcon,
  MessageSentIcon,
} from '../../components/Icons';
import Modal from '../../componentsv2/Modal';
import Snackbar from '../../components/Snackbar';
import SimpleBackdrop from '../../components/Backdrop';
import MagicCard from '../../components/MagicCard';
import { setFactoringActivity, useAppDispatch, useAppSelector } from '../../store';
import { useLazyTranslation } from '../../hooks/i18n';
import {
  deleteApplication,
  getApplications,
  getUnsignedMPSA,
  hasUnsignedMPSA,
  updateProductStatus,
} from '../../services/application';
import { getCompanyDocumentCategories } from '../../services/documents';
import { getCreditFacilities } from '../../services/servicing';
import { APIDocumentCategory } from '../../types/documents';
import { Nullable } from '../../types';
import { CompanyUser, InternalPipelineStatus, UserRole } from '../../types/companyUser';
import { APIApplication } from '../../types/application';
import { getPipelineStatus } from '../../utils/helpers';
import { APPLICATION, MY_DOCUMENTS, filesURL } from '../../constants/auth';
import { HELP_CENTER_URL } from '../../constants/common';
import { useProductLock } from '../../hooks/useProductLock';
import ProductEmptyState from '../../componentsv2/ProductEmptyState';
import {
  APPLICATION_APPROVED,
  APPLICATION_PENDING_SUBMIT,
  APPLICATION_STARTED,
  APPLICATION_REJECTED,
  APPLICATION_SUBMITTED,
} from '../../constants/variables';
import {
  creditLineEvents,
  invoiceFinancingEvents,
  servicingEvents,
  termSheetEvents,
} from '../../constants/events';
import { COLORS } from '../../themev2';
import { baseURL } from '../../constants/api';
import CreditLineModal from './CreditLineModal';
import MPSAChatDrawer from './MPSAChatDrawer';
import classes from './InvoiceFinancing.module.scss';
import { formatNumericValue } from '../../utils/common';
const Servicing = lazy(() => import('./Servicing'));

const FinancingStepHeader = ({
  title,
  header,
  description,
}: {
  title: string;
  header: string;
  description: string;
}) => {
  return (
    <Grid container rowSpacing={1}>
      <Grid item xs={12}>
        <Typography sx={{ color: COLORS.secondary.galacticGradient, fontSize: '16px' }} fontWeight={600}>
          {title}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h6" sx={{ color: COLORS.brand.night }} fontWeight={700}>
          {header}
        </Typography>
      </Grid>
      <Grid item xs={12} md={8}>
        <Typography sx={{ color: COLORS.gray[1200], fontSize: '16px' }}>{description}</Typography>
      </Grid>
    </Grid>
  );
};

const CustomAlert = styled(Alert)({
  '& .MuiAlert-icon': {
    color: COLORS.brand.paper,
  },
  background: '#0EA480',
  color: COLORS.brand.paper,
});

const BENEFITS = ['advanceInvoices', 'keepSelling', 'clearStatus'];

const STEPS = ['apply', 'getApproved', 'financeInvoices'];

const statusColorsMap: Record<string, object> = {
  onTheWay: { backgroundColor: '#E8F0FC', color: '#3B4567' },
  signed: { backgroundColor: '#E1FAED', color: '#11705F' },
  approved: { backgroundColor: '#E1FAED', color: '#11705F' },
  inReview: { backgroundColor: '#E8F0FC', color: '#3B4567' },
  denied: { backgroundColor: '#FEE3E3', color: '#930909' },
  pending: { backgroundColor: '#FCF0DB', color: '#AC8318' },
};

const proposalStatusMap: Record<InternalPipelineStatus, string> = {
  application_sent: 'notApplicable',
  application_received: 'onTheWay',
  termsheet_sent: 'pending',
  termsheet_received_and_document_collection: 'signed',
  due_dilligence: 'signed',
  credit_committee: 'signed',
  credit_committee_advanced: 'signed',
  legal: 'signed',
  onboarding: 'signed',
  closed: 'signed',
};

type InvoiceFinancingLocationState = {
  state: { openFundingRequest?: boolean };
};

const InvoiceFinancing = () => {
  const location = useLocation() as Location & InvoiceFinancingLocationState;
  const {
    isLoading: isLoadingTranslations,
    t,
    i18n,
  } = useLazyTranslation(['application', 'mpsa_chat', 'common']);
  const { isLocked, lockedHint } = useProductLock();
  const { selectedCompany } = useAppSelector((state) => state.companyUser.companyUser) || ({} as CompanyUser);
  const { isFactoringActive } = useAppSelector((state) => state.companyUser);
  const isVisualizer =
    useAppSelector((state) => state.companyUser.companyUser?.selectedCompanyRole) === UserRole.VISUALIZER;
  const dispatch = useAppDispatch();
  const [application, setApplication] = useState<Nullable<APIApplication>>(null);
  const [isLoadingApp, setIsLoadingApp] = useState(true);
  const [showDeleteAppModal, setShowDeleteAppModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [displayDeletedAppMessage, setDisplayDeletedAppMessage] = useState(false);
  const [displaySignedMessage, setDisplaySignedAppMessage] = useState(false);
  const [displayCreditLineApprovedMessage, setDisplayCreditLineApprovedMessage] = useState(false);
  const [showCreditLineDetails, setShowCreditLineDetails] = useState(false);
  const [documentCategories, setDocumentCategories] = useState<APIDocumentCategory[]>([]);
  const [openFundingRequest, setOpenFundingRequest] = useState(location.state?.openFundingRequest ?? false);
  const [isLoadingCreditFacilities, setIsLoadingCreditFacilities] = useState(false);
  const [unsignedMPSADocuments, setUnsignedMPSADocuments] = useState<any[]>([]);
  const [isLoadingMPSADocuments, setIsLoadingMPSADocuments] = useState(false);
  const [showUnsignedMPSAModal, setShowUnsignedMPSAModal] = useState(false);
  const [hasUnsignedMPSADoc, setHasUnsignedMPSADoc] = useState(false);
  const [showMPSAChatDrawer, setShowMPSAChatDrawer] = useState(false);
  const navigate = useNavigate();

  // Make sure the mpsa_chat namespace is loaded
  useEffect(() => {
    i18n
      .loadNamespaces(['mpsa_chat'])
      .catch((error: Error) => console.error('Failed to load mpsa_chat namespace:', error));
  }, [i18n]);

  // Get sidebar state from the global state
  const { showSidebar } = useAppSelector((state) => state.auth);

  const { creditCommittee, contractMpsa } = application || {};

  const camelizedInternalPipelineStatus = camelize(selectedCompany.internalPipelineStatus || '');
  const isLoading = isLoadingTranslations || isLoadingApp;

  const pipelineStatus = selectedCompany.internalPipelineStatus
    ? getPipelineStatus(selectedCompany.internalPipelineStatus)
    : '';

  const existsApplication = application?.status === APPLICATION_STARTED;
  const getApplicationCtaKey = () => {
    if (existsApplication) return 'application:howItWorks.continueAppButtonTitle';
    if (application?.status === APPLICATION_REJECTED) return 'application:howItWorks.reapplyButtonTitle';
    return 'application:emptyState.cta';
  };
  const applicationCtaKey = getApplicationCtaKey();
  const isServicingViewable = selectedCompany?.productStatus.readyForFactoring;
  const finishedApp =
    isServicingViewable ||
    application?.status === APPLICATION_SUBMITTED ||
    application?.status === APPLICATION_PENDING_SUBMIT ||
    application?.status === APPLICATION_APPROVED;
  const isTermsheetViewable = pipelineStatus === 'proposal';
  const isCreditContractViewable = pipelineStatus === 'contract';

  const docsSectionStatus =
    selectedCompany.internalPipelineStatus &&
    ['onboarding', 'credit_committee_advanced'].includes(selectedCompany.internalPipelineStatus)
      ? 'approved'
      : 'pending';

  const amount = formatNumericValue(creditCommittee?.creditLimit || 0, 0);

  // This effect is to clear the link state used to receive the request to open the funding drawer
  useEffect(() => {
    navigate(location.pathname, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check if the company has an unsigned MPSA document
  useEffect(() => {
    if (selectedCompany?.id && selectedCompany.internalPipelineStatus === 'legal') {
      checkForUnsignedMPSA(selectedCompany.id);
    }
  }, [selectedCompany]);

  const checkForUnsignedMPSA = (companyId: number) => {
    hasUnsignedMPSA(companyId)
      .then((hasDocument) => {
        setHasUnsignedMPSADoc(hasDocument);
      })
      .catch(() => {
        // Silent fail - we'll assume there's no document
        setHasUnsignedMPSADoc(false);
      });
  };

  const handleViewUnsignedMPSA = () => {
    if (selectedCompany?.id) {
      setIsLoadingMPSADocuments(true);
      getUnsignedMPSA(selectedCompany.id)
        .then((documents) => {
          if (documents && documents.length > 0) {
            setUnsignedMPSADocuments(documents);
            setShowMPSAChatDrawer(true);
          } else {
            // Show a message if no documents are found
            console.warn('No MPSA documents found for the company');
            setUnsignedMPSADocuments([]);
          }
        })
        .catch((error) => {
          console.error('Error fetching MPSA documents:', error);
          setUnsignedMPSADocuments([]);
        })
        .finally(() => {
          setIsLoadingMPSADocuments(false);
        });
    } else {
      // No company selected
      console.warn('No company selected');
    }
  };

  useEffect(() => {
    if (selectedCompany) {
      getApplications(selectedCompany)
        .then((applications) => {
          if (applications.length > 0) {
            setApplication({
              ...applications[0],
            });
          }
        })
        .finally(() => {
          setIsLoadingApp(false);
        });
    }
  }, [selectedCompany]);

  useEffect(() => {
    selectedCompany &&
      getCompanyDocumentCategories(selectedCompany.id).then((categories) => {
        setDocumentCategories(categories);
      });
  }, [selectedCompany]);

  useEffect(() => {
    if (
      selectedCompany.internalPipelineStatus === 'termsheet_received_and_document_collection' &&
      typeof selectedCompany?.productStatus.factoringFlowMetadata?.shouldDisplaySignatureModal === 'undefined'
    ) {
      setDisplaySignedAppMessage(true);
    }
  }, [
    selectedCompany.internalPipelineStatus,
    selectedCompany?.productStatus.factoringFlowMetadata?.shouldDisplaySignatureModal,
  ]);

  useEffect(() => {
    if (
      selectedCompany.internalPipelineStatus === 'credit_committee_advanced' &&
      typeof selectedCompany?.productStatus.factoringFlowMetadata?.shouldDisplayCreditLineModal ===
        'undefined'
    ) {
      setDisplayCreditLineApprovedMessage(true);
    }
  }, [
    selectedCompany.internalPipelineStatus,
    selectedCompany?.productStatus.factoringFlowMetadata?.shouldDisplayCreditLineModal,
  ]);

  useEffect(() => {
    if (
      typeof selectedCompany?.productStatus.factoringFlowMetadata?.shouldDisplayAllSetView !== 'undefined'
    ) {
      setIsLoadingCreditFacilities(true);
      getCreditFacilities(selectedCompany.id)
        .then((facilities) => {
          if (facilities.length > 0) {
            const isActive = facilities.some((facility) => facility.status === 'active');
            dispatch(setFactoringActivity(isActive));
          }
        })
        .finally(() => {
          setIsLoadingCreditFacilities(false);
        });
    }
  }, [
    selectedCompany,
    dispatch,
    selectedCompany?.productStatus.factoringFlowMetadata?.shouldDisplayAllSetView,
  ]);

  const handleDiscoverMore = () => {
    logEvent(invoiceFinancingEvents.CLICK_DISCOVER_MORE);
    window.open(HELP_CENTER_URL, '_blank', 'noopener,noreferrer');
  };

  const handleOnDeleteApplication = () => {
    if (application?.id) {
      setIsSubmitting(true);
      deleteApplication(application?.id)
        .then(() => {
          setApplication(null);
          setDisplayDeletedAppMessage(true);
          setShowDeleteAppModal(false);
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    }
  };

  const handleOnCloseSignedModal = () => {
    setDisplaySignedAppMessage(false);
    const payload = {
      flowMetadata: {
        ...selectedCompany?.productStatus.factoringFlowMetadata,
        shouldDisplaySignatureModal: false,
      },
    };
    if (application) {
      updateProductStatus(selectedCompany!.id, selectedCompany!.productStatus.id, payload).then(
        (updatedApp) => {
          logEvent(termSheetEvents.CLOSE_CONFIRMATION_MODAL);
          setApplication({ ...application, ...payload });
        }
      );
    }
  };

  const handleOnCloseCreditLineApprovedModal = () => {
    setDisplayCreditLineApprovedMessage(false);
    if (application) {
      const payload = {
        flowMetadata: {
          ...selectedCompany?.productStatus.factoringFlowMetadata,
          shouldDisplayCreditLineModal: false,
        },
      };
      updateProductStatus(selectedCompany!.id, selectedCompany!.productStatus.id, payload).then(
        (updatedApp) => {
          logEvent(creditLineEvents.CLOSE_CONFIRMATION_MODAL);
          setApplication({ ...application, ...payload });
        }
      );
    }
  };

  if (isLoading || isLoadingCreditFacilities) return <SimpleBackdrop hasOpaqueBackground isLoading />;

  return (
    <Layout>
      <Grid container rowSpacing={2}>
        {(finishedApp || isLoading) && (
          <Grid item xs={9}>
            <PageHeader title={t('application:title')} subtitle={t('application:subtitle')} />
          </Grid>
        )}
        {typeof selectedCompany?.productStatus.factoringFlowMetadata?.shouldDisplayAllSetView !==
          'undefined' &&
          !selectedCompany?.productStatus.factoringFlowMetadata?.shouldDisplayAllSetView &&
          isFactoringActive &&
          selectedCompany?.id &&
          !isVisualizer && (
            <Grid item xs={3} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'end' }}>
              <Box sx={{ height: '38px' }}>
                <FinanceInvoices
                  t={t}
                  event={servicingEvents.CLICK_FINANCE_INVOICES}
                  tKey="application"
                  companyId={selectedCompany?.id}
                  onClickFundingRequest={() => setOpenFundingRequest(true)}
                />
              </Box>
            </Grid>
          )}
        {isLoading ? (
          <Grid item xs={12}>
            <Skeleton variant="rectangular" height={350} animation="wave" />
          </Grid>
        ) : (
          <>
            {/* TODO: This condition will be refactored afterwards */}
            {isServicingViewable && (
              <Grid item xs={12}>
                <Servicing
                  flowMetadata={selectedCompany?.productStatus.factoringFlowMetadata!}
                  isOpenFundingRequestDrawer={openFundingRequest}
                  handleCloseFundingDrawer={() => setOpenFundingRequest(false)}
                />
              </Grid>
            )}
            {finishedApp && !isServicingViewable && (
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={12}>
                    <Section>
                      <Grid container>
                        {!!selectedCompany.internalPipelineStatus && (
                          <Grid item xs={12}>
                            <StatusTracker pipelineStatus={selectedCompany.internalPipelineStatus} />
                          </Grid>
                        )}
                        <Grid item xs={12}>
                          <Divider sx={{ borderColor: COLORS.gray[600] }} />
                        </Grid>
                        <Grid item xs={12}>
                          <FinancingStepHeader
                            title={t(`application:financingStep.${camelizedInternalPipelineStatus}.title`)}
                            header={t(
                              `application:financingStep.${camelizedInternalPipelineStatus}.header`,
                              pipelineStatus === 'contract' || pipelineStatus === 'financing'
                                ? {
                                    amount,
                                  }
                                : {}
                            )}
                            description={t(
                              `application:financingStep.${camelizedInternalPipelineStatus}.description`
                            )}
                          />
                        </Grid>
                      </Grid>
                    </Section>
                  </Grid>
                  {isTermsheetViewable && !!selectedCompany.internalPipelineStatus && (
                    <Grid item xs={12}>
                      <Section>
                        <Grid container alignItems="center">
                          <Grid item xs={12} md={2}>
                            <Chip
                              label={
                                <Typography fontWeight={600} sx={{ color: 'inherit' }}>
                                  {t(
                                    `application:statusChip.${
                                      proposalStatusMap[selectedCompany.internalPipelineStatus]
                                    }`
                                  )}
                                </Typography>
                              }
                              sx={{
                                ...statusColorsMap[proposalStatusMap[selectedCompany.internalPipelineStatus]],
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md>
                            <Typography sx={{ fontSize: '1.2em' }} fontWeight={600}>
                              {t('application:proposalSection.title')}
                            </Typography>
                            <Typography>
                              {t(
                                `application:proposalSection.description.${
                                  proposalStatusMap[selectedCompany.internalPipelineStatus]
                                }`
                              )}
                            </Typography>
                          </Grid>
                          <Grid
                            item
                            xs={12}
                            md={3}
                            sx={{
                              display: 'flex',
                              justifyContent: { xs: 'initial', md: 'flex-end' },
                              alignItems: 'center',
                              gap: '8px',
                            }}
                          >
                            {!isTermsheetViewable && (
                              <InfoButton
                                title={
                                  <Typography sx={{ color: '#FFF' }}>
                                    {t('application:proposalSection.tooltip')}
                                  </Typography>
                                }
                                placement="left"
                              />
                            )}
                            {isTermsheetViewable ? (
                              <Button
                                disabled={!application?.proposalLink}
                                startIcon={<EyeIcon />}
                                onClick={() => {
                                  selectedCompany.internalPipelineStatus === 'termsheet_sent'
                                    ? logEvent(termSheetEvents.VIEW_PROPOSAL)
                                    : logEvent(termSheetEvents.VIEW_SIGNED_PROPOSAL);
                                  application?.proposalLink &&
                                    window.open(application?.proposalLink, '_blank');
                                }}
                              >
                                {t('application:proposalSection.button')}
                              </Button>
                            ) : (
                              <Typography sx={{ color: COLORS.gray[1000], fontWeight: 600 }}>
                                {t('application:comingSoon')}
                              </Typography>
                            )}
                          </Grid>
                        </Grid>
                      </Section>
                    </Grid>
                  )}
                  {documentCategories.length > 0 && isTermsheetViewable && (
                    <Grid item xs={12}>
                      <Section>
                        <Grid container alignItems="center">
                          <Grid item xs={12} md={2}>
                            <Chip
                              label={
                                <Typography fontWeight={600} sx={{ color: 'inherit' }}>
                                  {t(`application:statusChip.${docsSectionStatus}`)}
                                </Typography>
                              }
                              sx={{
                                ...statusColorsMap[docsSectionStatus],
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md>
                            <Typography sx={{ fontSize: '1.2em' }} fontWeight={600}>
                              {t('application:documentsSection.title')}
                            </Typography>
                            <Typography>{t(`application:documentsSection.description.pending`)}</Typography>
                          </Grid>
                          <Grid
                            item
                            xs={12}
                            md={3}
                            sx={{
                              display: 'flex',
                              justifyContent: { xs: 'initial', md: 'flex-end' },
                              alignItems: 'center',
                              gap: '8px',
                            }}
                          >
                            <Button
                              onClick={() => {
                                navigate(MY_DOCUMENTS);
                              }}
                            >
                              {t('application:documentsSection.button')}
                            </Button>
                          </Grid>
                        </Grid>
                      </Section>
                    </Grid>
                  )}
                  {isCreditContractViewable && (
                    <>
                      <Grid item xs={12}>
                        <Section>
                          <Grid container alignItems="center">
                            <Grid item xs={12} md>
                              <Typography sx={{ fontSize: '1.2em' }} fontWeight={600}>
                                {t('application:creditSection.creditLine.title')}
                              </Typography>
                              <Typography>{t(`application:creditSection.creditLine.description`)}</Typography>
                            </Grid>
                            <Grid
                              item
                              xs={12}
                              md={3}
                              sx={{
                                display: 'flex',
                                justifyContent: { xs: 'initial', md: 'flex-end' },
                                alignItems: 'center',
                                gap: '8px',
                              }}
                            >
                              <Button
                                startIcon={<EyeIcon />}
                                disabled={!application?.creditCommittee}
                                onClick={() => {
                                  logEvent(creditLineEvents.VIEW_CREDIT_LINE_DETAILS);
                                  setShowCreditLineDetails(true);
                                }}
                              >
                                {t('application:creditSection.creditLine.button')}
                              </Button>
                            </Grid>
                          </Grid>
                        </Section>
                      </Grid>
                      <Grid item xs={12}>
                        <Section>
                          <Grid container alignItems="center">
                            <Grid item xs={12} md={7}>
                              <Typography sx={{ fontSize: '1.2em' }} fontWeight={600}>
                                {t('application:creditSection.contract.title')}
                              </Typography>
                              <Typography>
                                {t(
                                  `application:creditSection.contract.${
                                    contractMpsa?.recipients
                                      ? 'contractSentDescription'
                                      : 'awaitingContractDescription'
                                  }`
                                )}
                              </Typography>
                            </Grid>
                            <Grid
                              item
                              xs={12}
                              md={5}
                              sx={{
                                display: 'flex',
                                justifyContent: { xs: 'initial', md: 'flex-end' },
                                alignItems: 'center',
                                gap: '8px',
                              }}
                            >
                              {contractMpsa?.recipients ? (
                                <Grid container justifyContent="flex-end">
                                  {contractMpsa?.companyUserLink && (
                                    <Grid
                                      item
                                      xs={12}
                                      sx={{
                                        display: 'flex',
                                        justifyContent: { xs: 'initial', md: 'flex-end' },
                                      }}
                                    >
                                      <Button
                                        startIcon={<EyeIcon />}
                                        onClick={() => {
                                          logEvent(creditLineEvents.VIEW_CONTRACT);
                                          contractMpsa?.companyUserLink &&
                                            window.open(contractMpsa?.companyUserLink, '_blank');
                                        }}
                                      >
                                        {t('application:creditSection.contract.button')}
                                      </Button>
                                    </Grid>
                                  )}
                                </Grid>
                              ) : (
                                <Typography sx={{ color: COLORS.gray[1000], fontWeight: 600 }}>
                                  {t('application:comingSoon')}
                                </Typography>
                              )}
                            </Grid>
                            {selectedCompany.internalPipelineStatus === 'legal' && (
                              <>
                                <Grid item xs={12}>
                                  <Divider />
                                </Grid>
                                <Grid item xs={12}>
                                  <Grid container spacing={2}>
                                    {/* Add View Unsigned MPSA button if a document exists */}
                                    {hasUnsignedMPSADoc && (
                                      <Grid item xs={12}>
                                        <MagicCard
                                          gradientFrom="#4E57D3"
                                          gradientTo="#2EC4B6"
                                          gradientOpacity={0.7}
                                          gradientSize={250}
                                          gradientColor="#2EC4B6"
                                        >
                                          <Box
                                            sx={{
                                              padding: { xs: '1.2em', sm: '1.5em' },
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'space-between',
                                              background: 'rgba(255, 255, 255, 0.9)',
                                              backdropFilter: 'blur(8px)',
                                              flexDirection: { xs: 'column', sm: 'row' },
                                              gap: { xs: '12px', sm: '0px' },
                                            }}
                                          >
                                            <Typography
                                              sx={{
                                                fontWeight: 600,
                                                color: '#0A1334',
                                                fontSize: { xs: '14px', sm: '16px' },
                                                lineHeight: '24px',
                                                textAlign: { xs: 'center', sm: 'left' },
                                                width: { xs: '100%', sm: 'auto' },
                                              }}
                                            >
                                              {i18n.language.startsWith('es')
                                                ? 'Comprende tu Contrato con IA'
                                                : 'Understand Your Contract with AI'}
                                            </Typography>
                                            <Button
                                              startIcon={<SparklingFolderIcon />}
                                              onClick={handleViewUnsignedMPSA}
                                            >
                                              {i18n.language.startsWith('es') ? 'Chatear ahora' : 'Chat now'}
                                            </Button>
                                          </Box>
                                        </MagicCard>
                                      </Grid>
                                    )}
                                    {/* Contract recipients */}
                                    {contractMpsa?.recipients &&
                                      contractMpsa.recipients.length > 0 &&
                                      contractMpsa.recipients.map(
                                        ({ firstName, id, email, lastName, hasCompleted }) => {
                                          const status = hasCompleted ? 'signed' : 'pending';
                                          return (
                                            <Grid item xs={12} md key={id}>
                                              <Box
                                                sx={{
                                                  padding: '1.5em',
                                                  border: '1px solid #D7E1F5',
                                                  borderRadius: '8px',
                                                  height: '100%',
                                                }}
                                              >
                                                <Chip
                                                  label={
                                                    <Typography fontWeight={600} sx={{ color: 'inherit' }}>
                                                      {t(`application:statusChip.${status}`)}
                                                    </Typography>
                                                  }
                                                  sx={{
                                                    ...statusColorsMap[status],
                                                  }}
                                                />
                                                <Typography
                                                  sx={{
                                                    fontWeight: 600,
                                                    color: '#0A1334',
                                                    fontSize: '16px',
                                                    lineHeight: '24px',
                                                  }}
                                                >
                                                  {`${firstName} ${lastName}`}
                                                </Typography>
                                                <Typography sx={{ color: '#4F5A84', wordBreak: 'break-all' }}>
                                                  {email}
                                                </Typography>
                                              </Box>
                                            </Grid>
                                          );
                                        }
                                      )}
                                  </Grid>
                                </Grid>
                              </>
                            )}
                          </Grid>
                        </Section>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Grid>
            )}
            {!finishedApp && (
              <Grid item xs={12}>
                <ProductEmptyState
                  title={t('application:title')}
                  subtitle={t('application:emptyState.subtitle')}
                  benefits={BENEFITS.map((key) => t(`application:emptyState.benefits.${key}`))}
                  cta={
                    isVisualizer
                      ? undefined
                      : {
                          label: t(applicationCtaKey),
                          onClick: () => navigate(APPLICATION),
                          disabled: application?.isReapply && application?.status === APPLICATION_REJECTED,
                          isLocked,
                          tooltip: isLocked ? lockedHint : undefined,
                        }
                  }
                  secondaryCta={
                    existsApplication && !isVisualizer
                      ? {
                          label: t('application:howItWorks.deleteButtonTitle'),
                          onClick: () => setShowDeleteAppModal(true),
                          startIcon: <TrashIcon />,
                        }
                      : undefined
                  }
                  stepsTitle={t('common:productEmptyState.howDoesItWork')}
                  steps={STEPS.map((key) => ({
                    title: t(`application:emptyState.steps.${key}.title`),
                    description: t(`application:emptyState.steps.${key}.description`),
                  }))}
                  discoverMoreLabel={t('common:productEmptyState.discoverMore')}
                  onDiscoverMore={handleDiscoverMore}
                  heroSrc={`${filesURL}/${t('application:emptyState.heroImage')}`}
                />
              </Grid>
            )}
          </>
        )}
      </Grid>
      <Snackbar open={displayDeletedAppMessage} handleOnClose={() => setDisplayDeletedAppMessage(false)}>
        <CustomAlert severity="success" className={classes.alertMessage}>
          {t('application:deleteAppModal.successDeletionMessage')}
        </CustomAlert>
      </Snackbar>
      <Modal isOpen={showDeleteAppModal} handleClose={() => setShowDeleteAppModal(false)}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h5" sx={{ fontWeight: '600' }}>
              {t('application:deleteAppModal.title')}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography sx={{ color: '#66758F' }} variant="body1">
              {t('application:deleteAppModal.description')}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ borderColor: '#DDE6F6' }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Button
              fullWidth
              type="outline"
              onClick={() => {
                setShowDeleteAppModal(false);
              }}
            >
              {t('application:deleteAppModal.cancelButtonTitle')}
            </Button>
          </Grid>
          <Grid item xs={12} md={6}>
            <Button fullWidth color="danger" onClick={handleOnDeleteApplication} isLoading={isSubmitting}>
              {t('application:deleteAppModal.submitButtonTitle')}
            </Button>
          </Grid>
        </Grid>
      </Modal>
      {application?.creditCommittee && (
        <CreditLineModal
          creditCommittee={application.creditCommittee}
          isOpen={showCreditLineDetails}
          handleClose={() => {
            setShowCreditLineDetails(false);
          }}
        />
      )}
      <Modal handleClose={handleOnCloseSignedModal} isOpen={displaySignedMessage}>
        <Grid container spacing={2}>
          <Grid item xs={12} container justifyContent="center" alignItems="center">
            <MessageSentIcon />
          </Grid>
          <Grid item xs={12}>
            <Typography
              textAlign="center"
              variant="h5"
              sx={{ color: '#283952', fontWeight: '700', fontSize: '24px' }}
            >
              {t('application:signedModal.title')}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography textAlign="center" sx={{ color: '#71819C' }}>
              {t('application:signedModal.message')}
            </Typography>
          </Grid>
          <Grid item xs={12} sx={{ marginTop: '24px' }}>
            <Button fullWidth color="secondary" onClick={handleOnCloseSignedModal}>
              {t('application:signedModal.button')}
            </Button>
          </Grid>
        </Grid>
      </Modal>
      <Modal
        handleClose={handleOnCloseCreditLineApprovedModal}
        isOpen={displayCreditLineApprovedMessage}
        isAutoHeight
      >
        <Grid container spacing={2}>
          <Grid item xs={12} container justifyContent="center" alignItems="center">
            <StarIcon />
          </Grid>
          <Grid item xs={12}>
            <Typography
              textAlign="center"
              variant="h5"
              sx={{ color: '#283952', fontWeight: '700', fontSize: '24px' }}
            >
              {t('application:creditLineConfirmationModal.title')}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography
              textAlign="center"
              variant="h2"
              fontWeight={700}
              sx={{
                background:
                  'linear-gradient(74.44deg, #4E57D3 -10.53%, #2D95CD 46.22%, #2D96CD 46.23%, #2D96CC 46.24%, #2EC4B6 98.43%)',
                backgroundClip: 'text',
                '-webkit-background-clip': 'text',
                color: 'transparent',
              }}
            >
              {t('application:creditLineConfirmationModal.amount', { amount })}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography textAlign="center" sx={{ color: '#71819C' }}>
              {t('application:creditLineConfirmationModal.message')}
            </Typography>
          </Grid>
          <Grid item xs={12} sx={{ marginTop: '24px' }}>
            <Button fullWidth color="secondary" onClick={handleOnCloseCreditLineApprovedModal}>
              {t('application:creditLineConfirmationModal.button')}
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Typography textAlign="center" sx={{ color: '#71819C', fontSize: '12px' }}>
              {t('application:creditLineConfirmationModal.creditDisclaimer')}
            </Typography>
          </Grid>
        </Grid>
      </Modal>
      {/* Unsigned MPSA Documents Modal */}
      <Modal size="medium" isOpen={showUnsignedMPSAModal} handleClose={() => setShowUnsignedMPSAModal(false)}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          Your Contract with Marco
        </Typography>

        {isLoadingMPSADocuments ? (
          <Box sx={{ textAlign: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : unsignedMPSADocuments.length === 0 ? (
          <Typography>No unsigned MPSA documents available</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {unsignedMPSADocuments.map((doc) => (
              <Box
                key={doc.uuid}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 2,
                  border: '1px solid #D7E1F5',
                  borderRadius: '8px',
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 500 }}>{doc.fileName || 'Unsigned MPSA'}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Uploaded on {new Date(doc.createdAt).toLocaleDateString()}
                  </Typography>
                </Box>
                <Button
                  startIcon={<EyeIcon />}
                  onClick={() => {
                    window.open(`${baseURL}/assets/${doc.uuid}`, '_blank');
                  }}
                >
                  View Document
                </Button>
              </Box>
            ))}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button onClick={() => setShowUnsignedMPSAModal(false)}>Close</Button>
            </Box>
          </Box>
        )}
      </Modal>
      <MPSAChatDrawer
        isOpen={showMPSAChatDrawer}
        onClose={() => setShowMPSAChatDrawer(false)}
        documents={unsignedMPSADocuments}
        sidebarOpen={showSidebar}
        companyId={selectedCompany?.id}
      />
    </Layout>
  );
};

export default InvoiceFinancing;
